import { useState, useEffect, useMemo, useRef } from "react";

import './../Projects.css';

import { MapContainer, TileLayer, Popup, useMapEvents } from "react-leaflet";
import type { Map as LeafletMap, LatLngLiteral } from "leaflet";
import 'leaflet/dist/leaflet.css';

import { CapitalizeFirstLetter, listCountry, emptyCountry, ICountry } from "../../../fixedData";

import { SetStatusGlobal } from "../../../interface/Global";
import { SetModalData, SetModalState } from "../../../interface/PopUp";
import { GetDataPage, RegisterDataPage, UnRegisterDataPage } from "../../../interface/Data";

import { Svg_ArrowDetails, Svg_Cheked, Svg_Close, Svg_Delete, Svg_File, Svg_HighRise, Svg_HighRiseMultiple, Svg_Home, Svg_Link, Svg_LowRise, Svg_LowRiseMultiple, Svg_Masterplan, Svg_Search } from "../../../components/Svg";

import { OpenPage } from "../../../services/OpenPage";
import { CallbackError, CleanProjectLink, OpenFile, OpenProjectLink } from "../../../services/Function";
import { BkImageItem, Reg_Project } from "../../../services/Register";

import Comp_PageName from "../../../components/PageName";
import { Btn, Input_Text } from "../../../components/Input";
import Comp_Select, { SelectItem, SelectOption } from "../../../components/Select";

import { DataProject, ParseBkImage, ParseUnitTypeList } from "..";

interface DataClient {
    id             : number;
    comporate_name : string;
    qtd_projects   : string;
}

interface PositionStep {
    status   : boolean;
    finished : boolean;
}

const emptyOption: SelectOption = { value: 0, label: "Select an option", qtd_project: 0 };

const emptyBkImage: BkImageItem[] = [
    { file: null, status: false },
    { file: null, status: false },
    { file: null, status: false }
];

const listUnitType = [
    { value: "HOUSE",                 name: "House",        Icon: Svg_Home },
    { value: "LOW_RISE",              name: "Low-rise",     Icon: Svg_LowRise },
    { value: "LOW_RISE_MULTI_TOWER",  name: "Low-Rise\nMulti Tower",  Icon: Svg_LowRiseMultiple },
    { value: "HIGH_RISE",             name: "High-rise",    Icon: Svg_HighRise },
    { value: "HIGH_RISE_MULTI_TOWER", name: "High-Rise\nMulti Tower", Icon: Svg_HighRiseMultiple },
    { value: "MASTERPLAN",            name: "Masterplan",   Icon: Svg_Masterplan }
];

const containerStyle = { width: "100%", height: "680px" };

// Limite do mundo, equivalente ao restriction/strictBounds que existia no Google Maps
const worldBounds: [[number, number], [number, number]] = [[-85, -180], [85, 180]];

// Camadas de tile. Satélite enquanto não há posição definida, mapa claro depois de definida
const tileSatellite = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const tileSatelliteLabel = "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}";
const tileGray = "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";

const attribution = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> | Esri | CARTO';

// Chave do par código postal + país, normalizada para comparar com o que já foi buscado
function PostalKey(postalCode: string, sigla: string){
    return postalCode.replaceAll(" ", "").replaceAll("-", "").toLowerCase() + "|" + sigla;
}

// Preenche latitude e longitude ao clicar em qualquer ponto do mapa
function ClickToSetPosition(props: { onSelect: (position: LatLngLiteral) => void }){
    useMapEvents({
        click(e){
            props.onSelect({ lat: e.latlng.lat, lng: e.latlng.lng });
        }
    });
    return null;
}

export default function Page_Projects_Details() {

    const [ showData, setShowData ] = useState<{ [key: string]: DataProject }>(GetDataPage('projects') || {});
    const [ listClient, setListClient ] = useState<{ [key: string]: DataClient }>(GetDataPage('clients') || {});

    const [ idPage, setIdPage ] = useState<number>(localStorage.getItem('page_id') ? parseInt(localStorage.getItem('page_id') as string) : 0);

    const [ company, setCompany ] = useState<SelectOption>(emptyOption);
    const [ typeRegister, setTypeRegister ] = useState<SelectOption>(emptyOption);
    const [ nameProject, setNameProject ] = useState<string>('');
    const [ linkProject, setLinkProject ] = useState<string>('');
    const [ unitType, setUnitType ] = useState<string>('');
    const [ otherUnitType, setOtherUnitType ] = useState<string[]>([]);

    const [ latitude, setLatitude ] = useState<string>('');
    const [ longitude, setLongitude ] = useState<string>('');
    const [ countryName, setCountryName ] = useState<ICountry>(emptyCountry);
    const [ postalCode, setPostalCode ] = useState<string>('');

    // Última coordenada cujo país já foi resolvido, evita buscar o mesmo ponto duas vezes
    const lastLatLonRef = useRef<string>('');

    // Último CEP + país já casado com a coordenada acima. É o que diz, no clique do
    // Search, se quem mudou foi o código postal ou a coordenada.
    const lastPostalRef = useRef<string>('');

    const [ iconMapStatus, setIconMapStatus ] = useState<boolean>(false);
    const [ iconMap, setIconMap ] = useState<File | string | null>(null);
    const previewIconMap = useMemo(() => {
        if(iconMap){
            return iconMapStatus ? URL.createObjectURL(iconMap as File) : iconMap as string;
        }
        return null;
    }, [iconMap, iconMapStatus]);

    const [ logoStatus, setLogoStatus ] = useState<boolean>(false);
    const [ logo, setLogo ] = useState<File | string | null>(null);
    const previewLogo = useMemo(() => {
        if(logo){
            return logoStatus ? URL.createObjectURL(logo as File) : logo as string;
        }
        return null;
    }, [logo, logoStatus]);

    /* background image */
    const [ images, setImages ] = useState<BkImageItem[]>(emptyBkImage);
    const previews = useMemo(() =>
        images.map((elem) => {
            if(elem.file){
                return elem.status ? URL.createObjectURL(elem.file as File) : elem.file as string;
            }
            return null;
        }),
        [images]
    );
    const handleUpload = (index: number, files: FileList | null) => {
        if(!files || files.length === 0) return;

        setImages(prev => {
            const updated = [...prev];

            /* bloco clicado primeiro, depois os demais slots ainda vazios */
            const freeSlots = [ index, ...updated.map((_, i) => i).filter(i => i !== index) ].filter(i => !updated[i].file);

            /* pega apenas o que couber nas vagas livres, ignora o excedente */
            Array.from(files).slice(0, freeSlots.length).forEach((file, i) => {
                updated[freeSlots[i]] = { file: file, status: true };
            });

            return updated;
        });
    };
    const handleRemove = (index: number) => {
        setImages(prev => {
            const updated = [...prev];
            updated[index] = { file: null, status: false };
            return updated;
        });
    };
    /* end */

    /* map */
    const [ mapCenter ] = useState<LatLngLiteral>({ lat: 20, lng: 0 });
    const [ overlayPosition, setOverlayPosition ] = useState<LatLngLiteral | null>(null);
    const [ map, setMap ] = useState<LeafletMap | null>(null);
    /* end */

    const [ numberPosition, setNumberPosition ] = useState<number>(0);
    const [ position, setPosition ] = useState<{ [key: number]: PositionStep }>({
        0: { status: false, finished: false },
        1: { status: false, finished: false },
        2: { status: false, finished: false }
    });
    const updateStatus = (index: number, newStatus: boolean) => {
        setPosition(prev => ({ ...prev, [index]: { ...prev[index], status: newStatus } }));
    };
    const updateFinished = (index: number, newFinished: boolean) => {
        setPosition(prev => ({ ...prev, [index]: { ...prev[index], finished: newFinished } }));
    };

    // Lista de projetos já cadastrados para o cliente selecionado
    const qtdProjectCompany = useMemo(() =>
        Object.values(showData).filter(item => item.company === company.value).length,
        [showData, company]
    );

    function ShowListClient(): SelectItem[] {
        return Object.values(listClient).map((elem) => {
            return { id: elem.id, name: elem.comporate_name, qtd_project: parseInt(elem.qtd_projects) || 0 };
        });
    }

    function HandleSelectOptData(value: string) {
        const newData = [...otherUnitType];
        const index = newData.indexOf(value);
        if(index > -1){
            newData.splice(index, 1);
        }else {
            newData.push(value);
        }
        setOtherUnitType(newData);
    }

    // O link não pode guardar espaço nenhum — nem no começo, nem no meio, nem no
    // fim: ele vira url no servidor. A limpeza é na digitação, e não só no
    // salvar, para o campo mostrar exatamente o que vai ser gravado.
    function HandleLinkProject(value: string){
        setLinkProject(CleanProjectLink(value));
    }

    function ShowFile(status: boolean, value: File | string | null){
        if(!value) return;
        OpenFile('img', status ? URL.createObjectURL(value as File) : value as string);
    }

    function UploadFile(setValue: (value: File | string | null) => void, setValueStatus: (value: boolean) => void, value: File | undefined){
        if(value){
            setValue(value);
            setValueStatus(true);
        }else {
            setValue(null);
            setValueStatus(false);
        }
    }

    function OpenPosition(type: number){
        setNumberPosition(type);
        if(type === 0){
            updateStatus(0, true);
            updateStatus(1, false);
            updateStatus(2, false);
        }
        if(type === 1){
            updateStatus(0, true);
            updateStatus(1, true);
            updateStatus(2, false);
        }
        if(type === 2){
            updateStatus(0, true);
            updateStatus(1, true);
            updateStatus(2, true);
        }
    }

    function NextPosition(type: number){
        console.log("typeRegister", typeRegister)
        const titlePopup = 'Lack information';

        if(type === 0){
            // Create project
            if(typeRegister.value === 1){
                if(idPage === 0){
                    if(qtdProjectCompany < (company.qtd_project ?? 0)){
                        if(company.value === 0 && nameProject === '' && unitType === ''){
                            CallbackError(titlePopup, 'You need to choose the company, define the project name and choose the unit type.');

                        }else if(company.value === 0 && nameProject !== '' && unitType === ''){
                            CallbackError(titlePopup, 'You need to choose the company and the type of unit.');

                        }else if(company.value === 0 && nameProject === '' && unitType !== ''){
                            CallbackError(titlePopup, 'You need to choose the company and define the name of the project.');

                        }else if(company.value === 0 && nameProject !== '' && unitType !== ''){
                            CallbackError(titlePopup, 'You need to choose the company.');

                        }else if(company.value > 0 && nameProject !== '' && unitType === ''){
                            CallbackError(titlePopup, 'You need to set the unit type.');

                        }else if(company.value > 0 && nameProject === '' && unitType !== ''){
                            CallbackError(titlePopup, 'You need to define the project name.');

                        }else if(company.value > 0 && nameProject === '' && unitType === ''){
                            CallbackError(titlePopup, 'You need to define the project name and unit type.');

                        }else {
                            OpenPosition(type + 1);
                            updateFinished(type, true);
                            setNumberPosition(type + 1);
                        }

                    }else {
                        CallbackError(titlePopup, 'Project limit reached.');
                    }
                }else {
                    OpenPosition(type + 1);
                    updateFinished(type, true);
                    setNumberPosition(type + 1);
                }
            
            // Add project link
            }else if(typeRegister.value === 2){
                if(idPage === 0){
                    if(company.value === 0 && nameProject === '' && linkProject === ''){
                        CallbackError(titlePopup, 'You need to choose the company, define the project name and link project.');

                    }else if(company.value !== 0 && nameProject === '' && linkProject === ''){
                        CallbackError(titlePopup, 'You need to choose the name and link project');

                    }else if(company.value !== 0 && nameProject !== '' && linkProject === ''){
                        CallbackError(titlePopup, 'You need to choose the link project.');

                    }else if(company.value === 0 && nameProject !== '' && linkProject == ''){
                        CallbackError(titlePopup, 'You need to choose the company and link project.');

                    }else if(company.value === 0 && nameProject !== '' && linkProject !== ''){
                        CallbackError(titlePopup, 'You need to choose the company.');

                    }else {
                        OpenPosition(type + 1);
                        updateFinished(type, true);
                        setNumberPosition(type + 1);
                    }
                }else {
                    OpenPosition(type + 1);
                    updateFinished(type, true);
                    setNumberPosition(type + 1);
                }

            }else {
                CallbackError(titlePopup, 'You need to choose the company, define the project name and choose the unit type.');
            }
        }

        if(type === 1){
            if(latitude !== '' && longitude !== '' && iconMap){
                OpenPosition(type + 1);
                updateFinished(type, true);
                setNumberPosition(type + 1);
            }else {
                if(latitude !== '' && longitude !== '' && !iconMap){
                    CallbackError(titlePopup, 'It is necessary to add the icon to show on the map');
                }
            }
        }

        if(type === 2){
            const qtdBkImage = images.filter(item => item.file).length;
            if(logo && qtdBkImage > 0){
                OpenPosition(type + 1);
                updateFinished(type, true);
                setNumberPosition(type + 1);
            }else {
                if(!logo && qtdBkImage === 0){
                    CallbackError(titlePopup, 'It is necessary to add logo and background image');
                }else if(logo && qtdBkImage === 0){
                    CallbackError(titlePopup, 'It is necessary to add a background image');
                }else if(!logo && qtdBkImage > 0){
                    CallbackError(titlePopup, 'It is necessary to add the logo');
                }
            }
        }
    }

    /* map */
    // Busca país e código postal a partir da coordenada
    async function GetAddressByLatLon(lat: string = latitude, lon: string = longitude, showLoading: boolean = true){
        const key = lat + ',' + lon;
        lastLatLonRef.current = key;

        try {
            const res = await fetch('https://nominatim.openstreetmap.org/reverse?lat=' + lat + '&lon=' + lon + '&format=json&addressdetails=1');
            const data = await res.json();

            // Descarta a resposta se a posição já mudou enquanto a busca acontecia
            if(lastLatLonRef.current !== key) return;

            if(data && data.address){
                // Guarda o que ficou na tela para o Search saber, depois, se o usuário
                // mexeu no código postal ou na coordenada
                let syncSigla  = countryName.sigla;
                let syncPostal = postalCode;

                if(data.address.country){
                    const infCountry = listCountry.find(item => item.sigla === String(data.address.country_code).toUpperCase());
                    if(infCountry){
                        setCountryName(infCountry);
                        syncSigla = infCountry.sigla;
                    }
                }

                // Nem todo ponto tem código postal, então só sobrescreve quando vem preenchido
                if(data.address.postcode){
                    setPostalCode(String(data.address.postcode));
                    syncPostal = String(data.address.postcode);
                }

                lastPostalRef.current = PostalKey(syncPostal, syncSigla);
            }

        }catch (err){
            console.error("Erro ao buscar endereço:", err);

        }finally {
            if(showLoading){
                SetStatusGlobal('loading', false);
            }
        }
    }

    async function ShowDataCep(dataPostalCode: string){
        try {
            let address = "";
            if(countryName.name.toLowerCase() === "brazil"){
                const viaCepRes = await fetch(`https://viacep.com.br/ws/${ dataPostalCode }/json/`);
                const viaCepData = await viaCepRes.json();

                if(viaCepData.erro){
                    SetStatusGlobal('loading', false);
                    CallbackError('Lack of information', 'Postal code not found.');
                    return;
                }
                address = `${ viaCepData.logradouro || "" }, ${ viaCepData.localidade }, ${ viaCepData.uf }, Brasil`;

            }else {
                address = `${ dataPostalCode }, ${ countryName.name }`;
            }

            const nominatimRes = await fetch(`https://nominatim.openstreetmap.org/search?q=${ encodeURIComponent(address) }&format=json&limit=1`);
            const nominatimData = await nominatimRes.json();

            if(nominatimData.length > 0){
                const { lat, lon } = nominatimData[0];
                setLatitude(lat);
                setLongitude(lon);

                // O país já veio do próprio formulário, não precisa geocodificar de volta
                lastLatLonRef.current  = lat + ',' + lon;
                lastPostalRef.current  = PostalKey(dataPostalCode, countryName.sigla);

                const newPosition = { lat: parseFloat(lat), lng: parseFloat(lon) };
                map?.flyTo(newPosition, 16);

                setOverlayPosition(newPosition);
            }else {
                CallbackError('Lack of information', 'Postal code not found.');
            }

            SetStatusGlobal('loading', false);

        }catch (err){
            console.error("Erro ao buscar CEP:", err);
            SetStatusGlobal('loading', false);
        }
    }

    function SelectCountry(country: ICountry){
        setCountryName(country);
        if(!map || !country.sigla) return;

        const newPosition = { lat: country.latitude, lng: country.longitude };
        map.flyTo(newPosition, 4);
    }

    // Clique no mapa: preenche os campos e busca o endereço da posição escolhida
    function SelectPositionOnMap(position: LatLngLiteral){
        const lat = position.lat.toFixed(7);
        const lon = position.lng.toFixed(7);

        setLatitude(lat);
        setLongitude(lon);
        setOverlayPosition(position);

        SetStatusGlobal('loading', true);
        GetAddressByLatLon(lat, lon);
    }

    function SearchByPostalCode(){
        SetStatusGlobal('loading', true);
        ShowDataCep(postalCode.replaceAll(' ', '').replaceAll('-', ''));
    }

    function SearchByLatLon(){
        SetStatusGlobal('loading', true);
        const newPosition = { lat: parseFloat(latitude), lng: parseFloat(longitude) };
        map?.flyTo(newPosition, 16);

        GetAddressByLatLon();
        setOverlayPosition(newPosition);
    }

    function AddLonLat(){
        const hasLatLon = latitude !== '' && longitude !== '';
        const hasPostal = postalCode !== '' && countryName.sigla !== '';

        // Com os quatro campos preenchidos quem manda é o campo que o usuário mexeu:
        // os refs guardam o par coordenada/CEP que a última busca deixou casado.
        const latLonChanged = hasLatLon && lastLatLonRef.current !== latitude + ',' + longitude;
        const postalChanged = hasPostal && lastPostalRef.current !== PostalKey(postalCode, countryName.sigla);

        // Código postal alterado busca a coordenada mesmo com latitude e longitude preenchidas
        if(postalChanged && !latLonChanged){
            SearchByPostalCode();
            return;
        }

        // Coordenada alterada (ou nada alterado) refaz o endereço a partir dela
        if(hasLatLon){
            SearchByLatLon();
            return;
        }

        if(hasPostal){
            SearchByPostalCode();
            return;
        }

        CallbackError('Lack of information', 'Need to set latitude, longitude or postal code');
    }

    // Clicar no fundo escurecido (fora do painel) fecha igual ao botao Cancel.
    function handleBackdrop(event: React.MouseEvent<HTMLFormElement>){
        if(event.target === event.currentTarget){
            handleClose();
        }
    }

    function handleClose(){
        localStorage.removeItem('page_id');
        OpenPage('projects');
    }

    function ReturnProjectFinish(name: string, showLogoProject: string, linkReturn: string){
        SetModalData('ReturnResponse', { "page": "project_saved", "name_project": name, "logo": showLogoProject, "link_project": linkReturn });
        SetModalState('ReturnResponse', true);
    }

    function ReturnSaved(linkReturn: string, showLogoProject: string){
        localStorage.removeItem('page_id');
        OpenPage('projects');
        SetStatusGlobal('loading', false);

        SetModalData('CreationTime', {
            "ReturnProjectFinish" : ReturnProjectFinish,
            "onlyClose"           : false,
            "name_project"        : nameProject,
            "logo"                : showLogoProject,
            "link_project"        : linkReturn,
            "counterCreation"     : 15
        });
        SetModalState('CreationTime', true);
    }

    async function SaveData(event: React.FormEvent<HTMLFormElement>){
        event.preventDefault();
        SetStatusGlobal('loading', true);

        const response = await Reg_Project({
            idPage        : idPage,
            company       : company.value,
            typeRegister  : typeRegister.value,
            nameProject   : nameProject,
            linkProject   : linkProject,
            unitType      : unitType,
            otherUnitType : otherUnitType,
            latitude      : latitude,
            longitude     : longitude,
            countryName   : countryName.name,
            countrySigla  : countryName.sigla,
            postalCode    : postalCode,
            iconMap       : iconMap,
            logo          : logo,
            bkImage       : images
        });

        if(response.status_data){
            ReturnSaved(response.link_project || '', response.show_logo_project || '');

        }else {
            SetStatusGlobal('loading', false);
            CallbackError("Error saving information", "There was an issue saving the project details. Please try again.");
        }
    }

    // Preenche país e código postal automaticamente assim que latitude e longitude ficam válidas
    useEffect(()=>{
        if(latitude === '' || longitude === '') return;

        const lat = parseFloat(latitude);
        const lon = parseFloat(longitude);
        if(!Number.isFinite(lat) || !Number.isFinite(lon)) return;
        if(lat < -90 || lat > 90 || lon < -180 || lon > 180) return;

        // Posição já resolvida pelo clique no mapa, pelo CEP ou pelo carregamento do projeto
        if(lastLatLonRef.current === latitude + ',' + longitude) return;

        // Espera o usuário parar de digitar antes de consultar o Nominatim
        const timer = setTimeout(()=>{
            GetAddressByLatLon(latitude, longitude, false);
        }, 800);

        return ()=>{ clearTimeout(timer) };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [latitude, longitude]);
    /* end */

    useEffect(()=>{
        RegisterDataPage('projects', setShowData);
        RegisterDataPage('clients', setListClient);
        return ()=>{
            UnRegisterDataPage('projects', setShowData);
            UnRegisterDataPage('clients', setListClient);
        };
    }, []);

    useEffect(()=>{
        const currentId = localStorage.getItem('page_id') ? parseInt(localStorage.getItem('page_id') as string) : 0;
        setIdPage(currentId);

        const showInf = showData[currentId];
        if(!showInf){
            lastLatLonRef.current = '';
            lastPostalRef.current = '';
            setCompany(emptyOption);
            setTypeRegister(emptyOption);
            setNameProject('');
            setLinkProject('');
            setUnitType('');
            setOtherUnitType([]);
            setLatitude('');
            setLongitude('');
            setCountryName(emptyCountry);
            setPostalCode('');
            setIconMap(null);
            setLogo(null);
            setImages(emptyBkImage);
            return;
        }

        const infClient = listClient[showInf.company];
        setCompany({
            value       : showInf.company,
            label       : infClient ? infClient.comporate_name : (showInf.company_name || ''),
            qtd_project : infClient ? parseInt(infClient.qtd_projects) || 0 : 0
        });
        setTypeRegister({
            value : showInf.type_register,
            label : showInf.type_register === 1 ? 'Create project' : 'Add project link'
        });
        setNameProject(showInf.name_project || '');
        setLinkProject(showInf.link_project || '');
        setUnitType(showInf.unit_type || '');
        setOtherUnitType(ParseUnitTypeList(showInf.other_unit_type));

        setLatitude(showInf.latitude || '');
        setLongitude(showInf.longitude || '');
        // O país já vem salvo com o projeto, não precisa geocodificar no carregamento
        lastLatLonRef.current = (showInf.latitude || '') + ',' + (showInf.longitude || '');
        setCountryName(listCountry.find(item => item.sigla === showInf.country_sigla) || emptyCountry);
        setPostalCode(showInf.postal_code || '');
        lastPostalRef.current = PostalKey(showInf.postal_code || '', showInf.country_sigla || '');

        setIconMapStatus(false);
        setIconMap(showInf.icon_map || null);
        setLogoStatus(false);
        setLogo(showInf.logo || null);

        const listBk = ParseBkImage(showInf.bk_image);
        setImages(emptyBkImage.map((elem, index) => {
            return listBk[index] && listBk[index].file ? { file: listBk[index].file, status: false } : elem;
        }));

    }, [showData, listClient]);

    // Centraliza o mapa no projeto que está sendo editado
    useEffect(()=>{
        if(map && idPage > 0){
            const showInf = showData[idPage];
            if(showInf && showInf.latitude && showInf.longitude){
                const newPosition = { lat: parseFloat(showInf.latitude), lng: parseFloat(showInf.longitude) };
                map.flyTo(newPosition, 16);
                setOverlayPosition(newPosition);
            }
        }
    }, [map]);

    // Bloqueia o cadastro quando o cliente já atingiu o limite de projetos
    const blockNewProject = typeRegister.value === 1 && idPage === 0 && qtdProjectCompany >= (company.qtd_project ?? 0);

    // Etapa 1 fecha depois de salvo: empresa, tipo de registro, nome e tipos de
    // unidade já viraram pasta, banco e linha nos dois bancos, e trocar qualquer
    // um deles depois não teria como refazer o que o deploy montou.
    //
    // A única exceção é o link do projeto, e só no type register 2 ("Add project
    // link"): ali o link é dado, não gerado, então continua editável.
    const blockStepOne = idPage !== 0;

    return(
        <form className="page_details Page_Projects_Details" onSubmit={ SaveData } onClick={ handleBackdrop }>
            <div className="content_details">

                <Comp_PageName
                    title={ idPage === 0 ? "New Project" : "Edit Project" }
                    subtitle={ idPage === 0 ? "Enter the details of the new project" : "Edit project information" }
                />

                <div className="div_position">
                    <div className="position_number">
                        <div className={ position[0].status ? position[0].finished ? "number number_finished" : "number number_active" : "number number_active" }>
                            {
                                position[0].finished ? <Svg_Cheked className="icons icon_checkbox" color="#27AE60" /> : "1"
                            }
                        </div>
                        <div className={ position[0].status ? "text text_active" : "text" }>Project Info</div>
                    </div>

                    <div className="line_">
                        <div className={ position[1].status ? position[1].finished ? "div_line_color color_finished" : "div_line_color color_active" : "div_line_color" } />
                    </div>
                    <div className="position_number">
                        <div className={ position[1].status ? position[1].finished ? "number number_finished" : "number number_active" : "number" }>
                            {
                                position[1].finished ? <Svg_Cheked className="icons icon_checkbox" color="#27AE60" /> : "2"
                            }
                        </div>
                        <div className={ position[1].status ? "text text_active" : "text" }>Location</div>
                    </div>

                    <div className="line_">
                        <div className={ position[2].status ? position[2].finished ? "div_line_color color_finished" : "div_line_color color_active" : "div_line_color" } />
                    </div>
                    <div className="position_number">
                        <div className={ position[2].status ? position[2].finished ? "number number_finished" : "number number_active" : "number" }>
                            {
                                position[2].finished ? <Svg_Cheked className="icons icon_checkbox" color="#27AE60" /> : "3"
                            }
                        </div>
                        <div className={ position[2].status ? "text text_active" : "text" }>Logos</div>
                    </div>
                </div>

                <div className="inf_position">
                    {
                        /* etapa 3 - logo e imagens de capa */
                        position[2].status ?
                            <>
                                <div className="div_data_projec">
                                    <div className="inf_data">
                                        <div className="title_project">
                                            Upload project logo
                                        </div>
                                        <div className="subtitle_project remuve_margin_top">
                                            Insert a logo for the dashboard
                                        </div>
                                    </div>
                                    <div className="div_add_img_logo">
                                        {
                                            logoStatus || previewLogo ?
                                                <>
                                                    <div className="div_remuve_photo" onClick={ ()=>{ setLogo(null); setLogoStatus(false); } }>
                                                        <Svg_Delete className="icons" color="#FFFFFF" />
                                                    </div>
                                                    <div className="img_logo" onClick={ ()=>{ ShowFile(logoStatus, logo) } }>
                                                        <div className="show_img_logo" style={ { backgroundImage: `url("${ previewLogo }")` } } />
                                                    </div>
                                                </>
                                            :
                                            <label className="img_logo">
                                                <div className="icon_add_file">
                                                    <Svg_File className="icons" color="#464646" />
                                                </div>
                                                <div className="">Upload logo</div>

                                                <input type="file" onChange={ (e)=>{ UploadFile(setLogo, setLogoStatus, e.target.files ? e.target.files[0] : undefined) } } />
                                            </label>
                                        }
                                    </div>
                                </div>

                                <div className="div_data_projec">
                                    <div className="inf_data">
                                        <div className="title_project">
                                            Add Cover to card
                                        </div>
                                        <div className="subtitle_project remuve_margin_top">
                                            Insert image for card dashboard
                                        </div>
                                    </div>
                                </div>

                                <div className="div_data_projec list_bk_image">
                                    {
                                        images.map((elem, index)=>{
                                            if(elem.status || previews[index]){
                                                return(
                                                    <div className="div_add_img_logo" key={ index }>
                                                        <div className="div_remuve_photo" onClick={ ()=>{ handleRemove(index) } }>
                                                            <Svg_Delete className="icons" color="#FFFFFF" />
                                                        </div>
                                                        <div className="img_logo" onClick={ ()=>{ ShowFile(elem.status, elem.file) } }>
                                                            <div className="show_img_bk" style={ { backgroundImage: `url("${ previews[index] }")` } } />
                                                        </div>
                                                    </div>
                                                )
                                            }
                                            return(
                                                <div className="div_add_img_logo" key={ index }>
                                                    <label className="img_logo">
                                                        <div className="icon_add_file">
                                                            <Svg_File className="icons" color="#464646" />
                                                        </div>
                                                        <div className="">Upload cover { index + 1 }</div>

                                                        <input type="file" multiple onChange={ (e)=>{ handleUpload(index, e.target.files) } } />
                                                    </label>
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                            </>
                        :
                        /* etapa 2 - localização e ícone do mapa */
                        position[1].status ?
                            <div className="inf_map">
                                <div className="show_data_map">
                                    <div className="title_project_map">
                                        Position of your project
                                    </div>
                                    <div className="subtitle_project_map">
                                        Search your project by latitude and longitude
                                    </div>

                                    <div className="data_input">
                                        <Input_Text title="Latitude" type="text" value={ latitude } setValue={ setLatitude } placeholder="Enter latitude" required={ false } />

                                        <Input_Text title="Longitude" type="text" value={ longitude } setValue={ setLongitude } placeholder="Enter longitude" required={ false } />
                                    </div>

                                    <div className="subtitle_project_map">
                                        or by zip code
                                    </div>

                                    <div className="data_input">
                                        <Comp_Select title="Country" type="country" list={ listCountry } value={ countryName } setValue={ SelectCountry } required={ false } />

                                        <Input_Text title="Postal Code" type="text" value={ postalCode } setValue={ setPostalCode } placeholder="Enter postal code" required={ false } />
                                    </div>

                                    <div className="div_search" onClick={ ()=>{ AddLonLat() } }>
                                        <Svg_Search className="icons" color="#100F0D" />
                                        <div className="">Search</div>
                                    </div>
                                </div>

                                <div className="show_map">
                                    {
                                        <MapContainer
                                            style={ containerStyle }
                                            center={ mapCenter }
                                            zoom={ 2 }
                                            minZoom={ 2 }
                                            maxBounds={ worldBounds }
                                            maxBoundsViscosity={ 1 }
                                            zoomControl={ false }
                                            scrollWheelZoom={ true }
                                            ref={ setMap }
                                        >
                                            {
                                                overlayPosition ?
                                                    <TileLayer url={ tileGray } attribution={ attribution } noWrap={ true } />
                                                :
                                                <>
                                                    <TileLayer url={ tileSatellite } attribution={ attribution } noWrap={ true } />
                                                    <TileLayer url={ tileSatelliteLabel } noWrap={ true } />
                                                </>
                                            }

                                            <ClickToSetPosition onSelect={ SelectPositionOnMap } />

                                            {
                                                overlayPosition ?
                                                    <Popup
                                                        position={ overlayPosition }
                                                        className="map_overlay"
                                                        closeButton={ false }
                                                        autoClose={ false }
                                                        closeOnClick={ false }
                                                        autoPan={ false }
                                                    >
                                                        <div className="add_inf_logo_map">
                                                            <div className="point_map">
                                                                {
                                                                    iconMap ?
                                                                        <img alt="icon point" className="icon point img_fixed" src={ previewIconMap || '' } onClick={ ()=>{ ShowFile(iconMapStatus, iconMap) } } />
                                                                    :
                                                                    <img alt="icon point" src="./assets/point_map_1.png" className="icons" />
                                                                }
                                                                <img alt="icon point" src="./assets/point_map_0.png" className="icons" />
                                                            </div>

                                                            <div className="upload_file">
                                                                <div className="">Logo to map</div>
                                                                <label>
                                                                    <div className="show_inf_file">
                                                                        {
                                                                            iconMap ?
                                                                                <>
                                                                                    <img alt="icon point" className="icon point img_fixed" src={ previewIconMap || '' } />
                                                                                    <div className="name_file">
                                                                                        { iconMap instanceof File ? iconMap.name : 'Current Image' }
                                                                                    </div>
                                                                                </>
                                                                            :
                                                                            <>
                                                                                <Svg_File className="icons" color="#737574" />
                                                                                <div className="">Upload logo</div>
                                                                            </>
                                                                        }
                                                                    </div>
                                                                    <input type="file" onChange={ (e)=>{ UploadFile(setIconMap, setIconMapStatus, e.target.files ? e.target.files[0] : undefined) } } />
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </Popup>
                                                : null
                                            }
                                        </MapContainer>
                                    }
                                    {/* 
                                    <div className="hide_inf_map" /> 
                                    */}
                                </div>
                            </div>
                        :
                        /* etapa 1 - dados do projeto */
                        <>
                            <div className="title_project">
                                Enter the details of the new project
                            </div>
                            <div className="subtitle_project">
                                Select the company and enter the name of the project
                            </div>

                            <div className="div_inputs">
                                <div className={ typeRegister.value === 2 ? "show_div_input edit_margin_bottom" : "show_div_input" }>
                                    {
                                        blockStepOne ?
                                            <Comp_Select title="Company*" list={ [{ id: company.value, name: company.label }] } value={ company } setValue={ ()=>{ } } required={ true } />
                                        :
                                        <Comp_Select title="Company*" list={ ShowListClient() } value={ company } setValue={ setCompany } required={ true } />
                                    }

                                    {
                                        blockStepOne ?
                                            <Comp_Select title="Type register*" list={ [{ id: typeRegister.value, name: typeRegister.label }] } value={ typeRegister } setValue={ ()=>{ } } required={ true } />
                                        :
                                        <Comp_Select title="Type register*" list={ [{ id: 1, name: "Create project" }, { id: 2, name: "Add project link" }] } value={ typeRegister } setValue={ setTypeRegister } required={ true } />
                                    }

                                    {
                                        blockNewProject ? null
                                        :
                                        <Input_Text title="Project Name*" type="text" value={ nameProject } setValue={ setNameProject } placeholder="Enter project name" required={ true } readOnly={ blockStepOne } mask={ CapitalizeFirstLetter } />
                                    }
                                </div>
                                {
                                    /* o link é o único campo da etapa 1 que continua editável depois de
                                       salvo, e ele só aparece no type register 2 ("Add project link") */
                                    typeRegister.value === 2 ?
                                        <div className="show_div_input">
                                            {/* O espaço sai enquanto se digita, e não só ao salvar: o campo
                                                mostra exatamente o que vai virar url do outro lado. */}
                                            <Input_Text title="Link project*" type="text" value={ linkProject } setValue={ HandleLinkProject } placeholder="Enter project link" required={ true } />

                                            {
                                                /* com link digitado o ícone abre ele em outra aba, para
                                                   conferir o endereço antes de salvar */
                                                linkProject !== '' ?
                                                    <div className="open_link_project" onClick={ ()=>{ OpenProjectLink(linkProject) } } data-tooltip-id="show_alert" data-tooltip-content="Open project link" data-tooltip-place="top">
                                                        <Svg_Link className="icons" color="#EDA13F" />
                                                    </div>
                                                : null
                                            }
                                        </div>
                                    : null
                                }
                            </div>

                            {
                                typeRegister.value === 1 ?
                                    blockNewProject ?
                                        <div className="">
                                            Project limit reached.
                                        </div>
                                    :
                                    <>
                                        <div className="subtitle_project">
                                            Select the type of main project unit
                                        </div>
                                        <div className="list_inf_page">
                                            {
                                                listUnitType.map((elem, index)=>{
                                                    const Icon = elem.Icon;
                                                    return(
                                                        <div className={ unitType === elem.value ? "show_opt_select opt_selected" : "show_opt_select" } key={ index } onClick={ ()=>{ if(!blockStepOne){ setUnitType(elem.value) } } }>
                                                            <div className="checkbox">
                                                                {
                                                                    unitType === elem.value ?
                                                                        <Svg_Cheked className="icons icon_checkbox" color="#EDA13F" />
                                                                    : null
                                                                }
                                                            </div>
                                                            <div className="name_opt_click">
                                                                <div className="icon_click">
                                                                    <Icon className="icons" color="#B1B2B2" />
                                                                </div>
                                                                <div className="name_click">{ elem.name }</div>
                                                            </div>
                                                        </div>
                                                    )
                                                })
                                            }
                                        </div>

                                        <div className="subtitle_project">
                                            There is another type of unit in the project
                                        </div>
                                        <div className="list_inf_page" style={ { marginBottom: '30px' } }>
                                            {
                                                listUnitType.map((elem, index)=>{
                                                    const Icon = elem.Icon;
                                                    return(
                                                        <div className={ otherUnitType.includes(elem.value) ? "show_opt_select opt_selected" : "show_opt_select" } key={ index } onClick={ ()=>{ if(!blockStepOne){ HandleSelectOptData(elem.value) } } }>
                                                            <div className="checkbox">
                                                                {
                                                                    otherUnitType.includes(elem.value) ?
                                                                        <Svg_Cheked className="icons icon_checkbox" color="#EDA13F" />
                                                                    : null
                                                                }
                                                            </div>
                                                            <div className="name_opt_click">
                                                                <div className="icon_click">
                                                                    <Icon className="icons" color="#B1B2B2" />
                                                                </div>
                                                                <div className="name_click">{ elem.name }</div>
                                                            </div>
                                                        </div>
                                                    )
                                                })
                                            }
                                        </div>
                                    </>
                                : null
                            }
                        </>
                    }
                </div>

                <div className="div_btn div_row">
                    <Btn title="Cancel" type="btn_cancel" OpenFuncition={ handleClose } />

                    {
                        position[2].status ?
                            <Btn title="Save" />
                        :
                        <Btn title="Next" type="div" OpenFuncition={ ()=>{ NextPosition(numberPosition) } } />
                    }
                </div>
            </div>
        </form>
    )
}
